/**
 * Keeps the Hz product gallery rail in sync with Horizon's slideshow.
 */
class HzGalleryProgress extends HTMLElement {
  #attempts = 0;
  #animationFrame;
  #resizeObserver;
  #slideshow;
  #scroller;
  #input;
  #slideCount = 0;

  connectedCallback() {
    this.#input = this.querySelector('input[type="range"]');
    this.#connectToSlideshow();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#animationFrame);
    this.#slideshow?.removeEventListener('slideshow:select', this.#handleSelect);
    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#input?.removeEventListener('input', this.#handleInput);
    this.#resizeObserver?.disconnect();
  }

  #connectToSlideshow = () => {
    const mediaGallery = this.closest('media-gallery');
    const slideshow = mediaGallery?.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');

    if (!this.#input || !slideshow || !scroller) {
      if (this.#attempts < 30) {
        this.#attempts += 1;
        this.#animationFrame = requestAnimationFrame(this.#connectToSlideshow);
      }
      return;
    }

    this.#slideshow = slideshow;
    this.#scroller = scroller;
    this.#slideCount = Number(this.dataset.slideCount) || scroller.children.length;
    this.#input.max = String(Math.max(this.#slideCount - 1, 0));
    this.#input.value = '0';

    this.#slideshow.addEventListener('slideshow:select', this.#handleSelect);
    scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#input.addEventListener('input', this.#handleInput);

    this.#resizeObserver = new ResizeObserver(() => this.#updateFromSlideshow());
    this.#resizeObserver.observe(scroller);

    this.#updateFromSlideshow();
  };

  #handleSelect = (event) => {
    if (event.target !== this.#slideshow) return;
    this.#update(event.detail.index);
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = requestAnimationFrame(() => this.#updateFromSlideshow());
  };

  #handleInput = (event) => {
    const index = Number(event.currentTarget.value);
    if (Number.isFinite(index)) this.#slideshow?.select(Math.round(index), event);
  };

  #updateFromSlideshow() {
    if (!this.#slideshow) return;
    this.#update(this.#slideshow.current || 0);
  }

  #update(index) {
    const maxIndex = Math.max(this.#slideCount - 1, 1);
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), maxIndex);
    const progress = this.#slideCount > 1 ? safeIndex / (this.#slideCount - 1) : 0;
    const thumbOffset = progress * (1 - 1 / this.#slideCount) * 100;

    this.style.setProperty('--hz-gallery-progress-left', `${thumbOffset}%`);
    this.#input.value = String(Math.min(safeIndex, this.#slideCount - 1));
    this.#input.setAttribute('aria-valuetext', `Image ${safeIndex + 1} sur ${this.#slideCount}`);
  }
}

if (!customElements.get('hz-gallery-progress')) {
  customElements.define('hz-gallery-progress', HzGalleryProgress);
}
